import { Field, RichText, Text } from "@sitecore-jss/sitecore-jss-react";
import React, { Fragment } from "react";

import { yesOrNo } from "../../../Enums/enumValues";
import { DefaultCoverage } from "../../../interfaces/dup";
import { YesOrNo } from "../../../interfaces/enum";
import { ModelState } from "../../../interfaces/modelState";
import { PersonalDetailsModel } from "../../../providers/dupFlow/DupFlow.interfaces";
import styles from "../../../style/layout_components/Step1.module.scss";
import { Button } from "../../buttons/Button";
import { Card } from "../../card/Card";
import DateInput from "../../InputFields/DateInput";
import HorizontalRadioInput, { RadioOption } from "../../InputFields/HorizontalRadioInput";
import { V27, V48, V58 } from "../../InputFields/sanitizer/inputSanitizers";
import TextInput from "../../InputFields/TextInput";
import { V5, V6, V10, V11 } from "../../InputFields/validator/validators";

const MAX_AMOUNT_OF_CHILDREN = 8;
const MAX_AMOUNT_OF_ADULTS = 2;
const MAX_AMOUNT_OF_INSUREDS_ON_PURCHASE_MONEY_PRODUCT = 2;

interface Props {
  insuredPersons: Partial<PersonalDetailsModel>[];
  defaultCoverage: Partial<DefaultCoverage>;
  fields: Record<string, Field<string>>;
  modelState: ModelState | null;
  setModelState: (updatedModelState: ModelState) => void;
  onUpdateInsured: (index: number, data: Partial<PersonalDetailsModel>) => void;
  onUpdateInsuredDateOfBirth: (index: number, dateOfBirth: string) => void;
  onUpdateChildAsAdultPremium: (index: number, childAsAdultPremium: YesOrNo) => void;
  onAddInsuredPerson: (isChild: boolean) => void;
  onRemovePerson: (index: number) => void;
}

export const Insureds = ({
  insuredPersons,
  defaultCoverage,
  onAddInsuredPerson,
  onUpdateInsured,
  onUpdateInsuredDateOfBirth,
  onUpdateChildAsAdultPremium,
  onRemovePerson,
  fields,
  modelState,
  setModelState,
}: Props) => {
  const allowAddAll = (() => {
    if (defaultCoverage.insuranceType === "PurchaseMoneyProduct") {
      return insuredPersons.length < MAX_AMOUNT_OF_INSUREDS_ON_PURCHASE_MONEY_PRODUCT;
    } else {
      return true;
    }
  })();
  const allowAddChildren = insuredPersons.filter((a) => a.isChild).length < MAX_AMOUNT_OF_CHILDREN;
  const allowAddPartner = insuredPersons.filter((a) => !a.isChild).length < MAX_AMOUNT_OF_ADULTS;
  let childCount = 0;

  const onUpdateInsuredFirstName = (index: number) => (firstName: string) => {
    onUpdateInsured(index, { firstName });
  };

  const handleUpdateInsuredDateOfBirth = (index: number) => (dateOfBirth: string) => {
    onUpdateInsuredDateOfBirth(index, dateOfBirth);
  };

  const handleUpdateChildAsAdultPremium = (index: number) => (childAsAdultPremium: YesOrNo) => {
    onUpdateChildAsAdultPremium(index, childAsAdultPremium);
  };

  return (
    <Card>
      <h2>
        <Text field={fields.InsuredTitle} />
      </h2>
      {insuredPersons.map((insured, i) => {
        let firstnameFields = fields;
        const insuredId = insured.isChild ? "_child_" + ++childCount : i > 0 ? "_partner" : "";
        if (i > 0) {
          firstnameFields = {
            HeadingFirstname: insured.isChild ? fields.HeadingFirstnameChild : fields.HeadingFirstnamePartner,
            PlaceholderFirstname: fields.PlaceholderFirstname,
            InfoFirstname: i === 0 ? fields.InfoFirstname : { value: "" },
          };
        }
        return (
          <Fragment key={insured.personId}>
            {i > 0 && <div className="form-row-divider"></div>}
            <TextInput
              fieldData={firstnameFields}
              onBlur={onUpdateInsuredFirstName(i)}
              name="Firstname"
              inputValue={insured.firstName}
              modelState={modelState}
              setModelState={setModelState}
              sanitize={[V27, V48, V58]}
              validators={[V5, V10(35), V11(2)]}
              propertyName={`model.InsuredPersons[${i}].FirstName`}
              fieldName={`Insured[${i}].FirstName`}
              id={`insured_firstname${insuredId}`}
              required
            />
            <DateInput
              fieldData={fields}
              onChange={handleUpdateInsuredDateOfBirth(i)}
              name="DateOfBirth"
              inputValue={insured.dateOfBirth}
              modelState={modelState}
              setModelState={setModelState}
              validators={[V5, V6]}
              propertyName={`model.InsuredPersons[${i}].DateOfBirth`}
              fieldName={`Insured[${i}].DateOfBirth`}
              id={`insured_dob${insuredId}`}
              required
            />
            {insured.isChild && (
              <HorizontalRadioInput
                fieldData={fields}
                name="ChildAsAdultPremium"
                fieldName={`Insured[${i}].ChildAsAdultPremium`}
                selectedValue={insured.childAsAdultPremium}
                options={yesOrNo() as RadioOption<YesOrNo>[]}
                validators={[V5]}
                modelState={modelState}
                propertyName={`model.InsuredPersons[${i}].ChildAsAdultPremium`}
                onChange={handleUpdateChildAsAdultPremium(i)}
                id={`insured_child_adult_premium_${childCount}`}
              />
            )}
            {i > 0 && (
              <div className={`form-row ${styles.deletePersonButtonsWrapper}`}>
                <Button
                  id={`remove_${insured.isChild ? "child_" + childCount : "partner"}`}
                  type="link"
                  leading={<i className={`${styles.deleteAdditionalInsuredIcon} icon-prullebak`} />}
                  onClick={() => onRemovePerson(i)}
                >
                  <Text field={fields.LabelDeletePerson} />
                </Button>
              </div>
            )}
            {i + 1 === insuredPersons.length && (
              <>
                <div className="form-row-divider"></div>
                <div className="form-row">
                  <div className="form-item">
                    <span>
                      <RichText field={fields.DescriptionAddPerson} />
                    </span>
                  </div>
                  <div className={styles.addPersonButtonsWrapper}>
                    <Button
                      id="add_person"
                      type="link"
                      disabled={!(allowAddPartner && allowAddAll)}
                      leading={<i className={`${styles.addPartnerIcon} icon-gebruiker-toevoegen`} />}
                      onClick={() => onAddInsuredPerson(false)}
                    >
                      <Text field={fields.LabelAddPartner} />
                    </Button>

                    <Button
                      id="add_child"
                      type="link"
                      disabled={!(allowAddChildren && allowAddAll)}
                      leading={<i className={`${styles.addChildIcon} icon-gebruiker-kind-toevoegen`} />}
                      onClick={() => onAddInsuredPerson(true)}
                    >
                      <Text field={fields.LabelAddChild} />
                    </Button>
                  </div>
                </div>
              </>
            )}
          </Fragment>
        );
      })}
    </Card>
  );
};
